import { Store } from 'vuex'
import store from '../root.store'

import {
    VuexModuleAccessor,
    VuexModuleStoreInstance,
    VuexRegisterGlobalOptions,
    VuexRegisterGlobalActionParams
} from '@/commons/types'

function registerActions<T, K> (
    actionDefinitions: Array<VuexRegisterGlobalActionParams>,
    targetStore: Store<T>,
    localStore: VuexModuleAccessor<K>): void {
    if (actionDefinitions.length < 1) {
        return
    }
    for (const { targetActionName, contextActionName } of actionDefinitions) {
        const targetActions = (targetStore as unknown as VuexModuleStoreInstance<T>)._actions
        const functionEntries = targetActions[targetActionName] || (targetActions[targetActionName] = [])
        const wrappedActionHandler = function<S> (payload: S) {
            const handler = (localStore[contextActionName] as (...args : unknown[]) => unknown).call(localStore, payload)
            // Handler should be promise, if handler is already promise then return pure handler
            return (typeof handler === 'object' && handler instanceof Promise) ? handler : Promise.resolve(handler)
        }
        functionEntries.push(wrappedActionHandler)
    }
}

/*
* This function is used as class decorator for Vuex module
* It is needed for register global actions in namespaced modules: https://vuex.vuejs.org/guide/modules.html
*/
export function VuexRegisterGlobals (options: VuexRegisterGlobalOptions): ClassDecorator {
    return function (constructor: unknown): (<T, K> (this: T, ...args: Array<K>) => T) {
        const originalClass = constructor
        // the new constructor behaviour
        const decoratedClass = function<T, K> (this: T, ...args: Array<K>) {
            if (typeof originalClass === 'function') {
                const instance = originalClass.apply(this, args)
                registerActions(options.actionParams, store, instance)
                return instance
            }
            return null
        }
        // copy prototype so "instanceof" operator still works
        if (typeof originalClass === 'function') {
            decoratedClass.prototype = originalClass.prototype
        }
        // return new constructor (will override original)
        return decoratedClass
    } as ClassDecorator
}
